import '../styles/theme.css';
import { ThemeManager } from './theme';
import { I18nManager } from './i18n';
import { fetchActivePricing } from './pricing/load-pricing';
import { ContactFormManager } from './contact';
import { resolveLocalizedUrl } from './routes';
import { readVisitAttribution } from './attribution/classify';
import { decorateLinks, updateRegistrationInterval } from './attribution/decorate';

class Application {
  private theme: ThemeManager;
  private i18n: I18nManager;
  private contactForm: ContactFormManager;
  private activeFeatureTab: string = 'orderChain';
  private billingInterval: 'monthly' | 'yearly' = 'monthly';

  constructor() {
    // English mode is temporarily deactivated for go-live (German only, see issue #1174).
    // Redirect any direct /en/ route visits to their German counterpart.
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/en/') || currentPath === '/en') {
        const targetUrl = resolveLocalizedUrl(window.location.pathname + window.location.search + window.location.hash, 'de');
        window.location.replace(targetUrl);
        return;
      }
    }

    this.theme = new ThemeManager();
    this.i18n = new I18nManager();
    this.contactForm = new ContactFormManager(this.i18n);

    this.initEventListeners();
    this.initAppLinks();
    this.initAttribution();
    this.initActiveNavLinks();
    this.initFeatureTabs();
    this.initPricingToggle();
    this.initLivePricing();
    this.initScrollReveal();

    // Enable hover/interaction transitions only after initial layout paint completes
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.classList.add('js-ready');
        });
      });
    } else {
      document.documentElement.classList.add('js-ready');
    }
  }

  private initScrollReveal(): void {
    const observerOptions: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.08,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll('.reveal-on-scroll, .reveal-scale, .reveal-fade');
    revealElements.forEach((el) => observer.observe(el));
  }

  private initEventListeners(): void {
    // Theme toggle buttons
    const themeBtns = document.querySelectorAll<HTMLButtonElement>('.theme-toggle-btn');
    themeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.theme.toggleTheme();
      });
    });

    // Language selector links (real <a href> so /en/ pages stay crawlable without JS;
    // JS intercepts the click to switch in place where possible instead of a full reload).
    const langBtns = document.querySelectorAll<HTMLAnchorElement>('.lang-select-btn');
    langBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetLang = btn.getAttribute('data-lang') as 'de' | 'en';
        if (!targetLang) return;

        localStorage.setItem('crm-lang', targetLang);

        const currentPath = window.location.pathname;
        const isCurrentlyEn = currentPath.startsWith('/en/') || currentPath === '/en' || currentPath.startsWith('/en.');
        const isAlreadyTarget = (targetLang === 'en' && isCurrentlyEn) || (targetLang === 'de' && !isCurrentlyEn);

        if (!isAlreadyTarget) {
          const currentFull = window.location.pathname + window.location.search + window.location.hash;
          const targetUrl = resolveLocalizedUrl(currentFull, targetLang);
          window.location.href = targetUrl;
        } else {
          this.i18n.setLanguage(targetLang);
          this.updateFeatureTabContent();
          this.updatePricingPrices();
        }
      });
    });

    // Mobile Menu Toggle with Accessibility & Auto-Close
    const mobileMenuBtn = document.getElementById('mobile-menu-btn');
    const mobileNav = document.getElementById('mobile-nav');

    if (mobileMenuBtn && mobileNav) {
      mobileMenuBtn.setAttribute('aria-expanded', 'false');
      mobileMenuBtn.setAttribute('aria-controls', 'mobile-nav');
      mobileMenuBtn.setAttribute('aria-label', 'Toggle navigation menu');

      const toggleMenu = (open?: boolean) => {
        const shouldOpen = open !== undefined ? open : mobileNav.classList.contains('hidden');
        if (shouldOpen) {
          mobileNav.classList.remove('hidden');
          mobileMenuBtn.setAttribute('aria-expanded', 'true');
        } else {
          mobileNav.classList.add('hidden');
          mobileMenuBtn.setAttribute('aria-expanded', 'false');
        }
      };

      mobileMenuBtn.addEventListener('click', () => toggleMenu());

      // Auto-close menu when clicking links inside mobile drawer
      mobileNav.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => toggleMenu(false));
      });

      // Close menu on Escape key press
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !mobileNav.classList.contains('hidden')) {
          toggleMenu(false);
        }
      });
    }

    // Re-update tab content on language change event
    window.addEventListener('languageChange', () => {
      this.updateFeatureTabContent();
      this.updatePricingPrices();
    });
  }

  /**
   * Hydrate all [data-app-href] anchors with their real href at runtime.
   * External app URLs are stored in the data attribute rather than in the HTML href
   * so the static source contains no third-party hrefs (DATA-004 compliance).
   */
  private initAppLinks(): void {
    document.querySelectorAll<HTMLAnchorElement>('[data-app-url]').forEach((el) => {
      const url = el.getAttribute('data-app-url');
      if (url) {
        el.href = url;
        el.rel = 'noopener noreferrer';
        el.target = '_blank';
      }
    });
  }

  /**
   * Derive where this visit came from and write it into the links the visitor
   * can click — our own pages and the registration calls to action. The visit
   * travels in the URL and nowhere else: nothing is written to the browser's
   * storage, and the referring host never leaves this function (ARCH-002
   * amendment *Closed-vocabulary attribution and operator-registered links*).
   *
   * Runs after initAppLinks(), which hydrates every [data-app-url] anchor with
   * its bare URL; the registration ones are rewritten here with the visit.
   */
  private initAttribution(): void {
    decorateLinks(
      document,
      readVisitAttribution({
        search: window.location.search,
        referrer: document.referrer,
        host: window.location.hostname,
      }),
      this.billingInterval,
    );
  }

  /**
   * Marks the nav/footer link pointing at the current page as active. The
   * shared header/footer partials (src/partials/) are identical on every
   * page, so which link "is" the current page can only be known at runtime.
   * Section-anchor links (href containing "#", e.g. "/#features") are left
   * alone — they are same-page shortcuts, not distinct pages.
   */
  private initActiveNavLinks(): void {
    const normalize = (pathname: string): string => {
      const file = pathname.split('/').pop() || '';
      const clean = file.replace(/\.html$/, '');
      return clean === '' || clean === 'index' ? '' : clean;
    };

    const currentPage = normalize(window.location.pathname);
    const links = document.querySelectorAll<HTMLAnchorElement>(
      'header nav a[href], header #mobile-nav a[href], footer ul a[href]'
    );

    links.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href.includes('#') || href.startsWith('http')) return;

      if (normalize(href) === currentPage) {
        link.classList.add('text-primary', 'font-semibold');
        link.setAttribute('aria-current', 'page');
      }
    });
  }

  private initFeatureTabs(): void {
    const tabBtns = document.querySelectorAll<HTMLButtonElement>('.feature-tab-btn');
    tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const tabKey = btn.getAttribute('data-tab');
        if (!tabKey) return;

        this.activeFeatureTab = tabKey;
        
        tabBtns.forEach((b) => {
          const isSelected = b.getAttribute('data-tab') === tabKey;
          b.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          if (isSelected) {
            b.classList.add('bg-primary', 'text-white', 'shadow-lg');
            b.classList.remove('bg-surface-glass', 'text-text-secondary', 'hover:bg-surface-3');
          } else {
            b.classList.remove('bg-primary', 'text-white', 'shadow-lg');
            b.classList.add('bg-surface-glass', 'text-text-secondary', 'hover:bg-surface-3');
          }
        });

        this.updateFeatureTabContent();
      });
    });

    this.updateFeatureTabContent();
  }

  private updateFeatureTabContent(): void {
    const dict = this.i18n.getTranslations();
    const itemData = dict.features.items[this.activeFeatureTab as keyof typeof dict.features.items];
    if (!itemData) return;

    const showcaseContainer = document.getElementById('feature-showcase-container');

    const updateDOM = () => {
      const titleEl = document.getElementById('feature-detail-title');
      const descEl = document.getElementById('feature-detail-desc');
      const bulletsEl = document.getElementById('feature-detail-bullets');

      if (titleEl) titleEl.textContent = itemData.title;
      if (descEl) descEl.textContent = itemData.desc;
      if (bulletsEl) {
        bulletsEl.innerHTML = itemData.bullets
          .map(
            (b) => `
          <li class="flex items-start gap-3">
            <div class="p-1 rounded-full bg-primary/10 text-primary mt-0.5">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
            </div>
            <span class="text-sm font-medium text-text-primary">${b}</span>
          </li>
        `
          )
          .join('');
      }

      const previewEl = document.getElementById('feature-mockup-preview');
      if (previewEl) {
        previewEl.innerHTML = this.renderMockupForTab(this.activeFeatureTab);
      }
    };

    if (showcaseContainer) {
      showcaseContainer.classList.add('tab-out');
      setTimeout(() => {
        updateDOM();
        showcaseContainer.classList.remove('tab-out');
        showcaseContainer.classList.add('tab-in');
        setTimeout(() => showcaseContainer.classList.remove('tab-in'), 300);
      }, 150);
    } else {
      updateDOM();
    }
  }

  private renderMockupForTab(tab: string): string {
    const isEn = this.i18n.getLanguage() === 'en';

    if (tab === 'orderChain' || tab === 'invoices') {
      return `
        <div class="glass-card p-6 border border-border space-y-4 font-sans text-xs">
          <div class="flex items-center justify-between border-b border-border pb-4">
            <div>
              <div class="flex items-center gap-2">
                <span class="badge-brand px-2.5 py-1 rounded-full text-[11px]">${isEn ? 'Order Chain Active' : 'Auftragskette Aktiv'}</span>
                <span class="px-2.5 py-1 rounded-full bg-success/15 text-success font-semibold text-[11px]">${isEn ? 'Paid' : 'Bezahlt'}</span>
              </div>
              <h4 class="text-base font-bold text-text-primary mt-1">${isEn ? 'Order #AK-2026-0042 • Acme Corp GmbH' : 'Auftrag #AK-2026-0042 • Acme Corp GmbH'}</h4>
              <p class="text-text-muted">${isEn ? 'Seamless sequence: Quote → Confirmation → Delivery Note → Invoice' : 'Lückenlose Kette: Angebot → Auftragsbestätigung → Lieferschein → Rechnung'}</p>
            </div>
            <div class="text-right flex-shrink-0 ml-4">
              <span class="text-text-muted text-[10px] block">${isEn ? 'Invoice Total' : 'Rechnungsbetrag'}</span>
              <p class="text-lg font-extrabold text-text-primary whitespace-nowrap">4.748,10 €</p>
            </div>
          </div>

          <!-- Flow Pipeline Steps -->
          <div class="grid grid-cols-4 gap-2 text-center text-[11px]">
            <div class="p-2 rounded-lg bg-surface-2 border border-border">
              <span class="text-success font-bold block">✓ ${isEn ? 'Quote' : 'Angebot'}</span>
              <span class="text-text-muted text-[10px]">ANG-0210 (${isEn ? 'Signed' : 'Signiert'})</span>
            </div>
            <div class="p-2 rounded-lg bg-surface-2 border border-border">
              <span class="text-success font-bold block">✓ ${isEn ? 'Order Conf.' : 'Bestätigung'}</span>
              <span class="text-text-muted text-[10px]">AB-2026-0042</span>
            </div>
            <div class="p-2 rounded-lg bg-surface-2 border border-border">
              <span class="text-success font-bold block">✓ ${isEn ? 'Delivery Note' : 'Lieferschein'}</span>
              <span class="text-text-muted text-[10px]">LS-0018 (100%)</span>
            </div>
            <div class="p-2 rounded-lg bg-primary/10 border border-primary/30">
              <span class="text-primary font-bold block">✓ ${isEn ? 'Final Invoice' : 'Schlussrechnung'}</span>
              <span class="text-text-muted text-[10px]">RE-2026-0842</span>
            </div>
          </div>

          <div class="space-y-2 pt-1">
            <div class="flex justify-between p-2.5 rounded-lg bg-surface-2">
              <span class="font-medium text-text-primary">${isEn ? '1. Software Development (Senior Dev Rate)' : '1. Software-Entwicklung (Senior Dev Rate)'}</span>
              <span class="font-semibold text-text-primary">${isEn ? '32 hrs × €110.00 = €3,520.00' : '32 Std. × 110,00 € = 3.520,00 €'}</span>
            </div>
            <div class="flex justify-between p-2.5 rounded-lg bg-surface-2">
              <span class="font-medium text-text-primary">${isEn ? '2. Server Module Hardware (Delivery LS-0018)' : '2. Server-Modul Hardware (Lieferung LS-0018)'}</span>
              <span class="font-semibold text-text-primary">${isEn ? '1 Hub × €470.00 = €470.00' : '1 Hub × 470,00 € = 470,00 €'}</span>
            </div>
          </div>

          <div class="flex justify-between items-center text-text-secondary pt-2 border-t border-border">
            <span>${isEn ? 'Subtotal: €3,990.00 • VAT (19%): €758.10' : 'Netto: 3.990,00 € • USt (19%): 758,10 €'}</span>
            <span class="font-bold text-primary inline-flex items-center gap-1">${isEn ? 'EN 16931 / XRechnung Validated' : 'EN 16931 / XRechnung Validiert'} <svg class="w-3.5 h-3.5 text-success inline-block" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg></span>
          </div>
        </div>
      `;
    } else if (tab === 'inventory') {
      return `
        <div class="glass-card p-6 border border-border space-y-4 font-sans text-xs">
          <div class="flex items-center justify-between border-b border-border pb-4">
            <div>
              <span class="badge-brand px-2.5 py-1 rounded-full text-[11px]">${isEn ? 'Multi-Warehouse & Stock' : 'Warenwirtschaft & Multi-Lager'}</span>
              <h4 class="text-base font-bold text-text-primary mt-1">${isEn ? 'Inventory & Procurement Cockpit' : 'Bestandsübersicht & Beschaffung'}</h4>
              <p class="text-text-muted">${isEn ? 'Article #ART-1042 • Industrial Router Pro' : 'Artikel #ART-1042 • Industrie-Router Pro'}</p>
            </div>
            <div class="text-right">
              <span class="px-2.5 py-1 rounded-full bg-primary/10 text-primary font-semibold text-[11px]">${isEn ? '2 Locations' : '2 Lagerorte'}</span>
              <p class="text-lg font-extrabold text-text-primary mt-1">${isEn ? 'Total: 51 Units' : 'Gesamt: 51 Stk.'}</p>
            </div>
          </div>

          <div class="space-y-2">
            <div class="p-3 rounded-xl bg-surface-2 border border-border flex justify-between items-center">
              <div>
                <span class="font-bold text-text-primary block">${isEn ? 'Main Depot (Frankfurt)' : 'Hauptlager (Frankfurt)'}</span>
                <span class="text-text-muted text-[11px]">${isEn ? '48 units in stock • Min. threshold: 20' : '48 Stk. auf Lager • Mindestbestand: 20 Stk.'}</span>
              </div>
              <span class="px-2.5 py-1 rounded-full bg-success/20 text-success font-semibold text-[11px]">${isEn ? 'Stock OK' : 'Bestand OK'}</span>
            </div>

            <div class="p-3 rounded-xl bg-surface-2 border border-border flex justify-between items-center">
              <div>
                <span class="font-bold text-text-primary block">${isEn ? 'Service Van 1 (Mobile Store)' : 'Service-Fahrzeug Van 1'}</span>
                <span class="text-text-muted text-[11px]">${isEn ? '3 units in stock • Min. threshold: 10' : '3 Stk. auf Lager • Mindestbestand: 10 Stk.'}</span>
              </div>
              <span class="px-2.5 py-1 rounded-full bg-warning/20 text-warning font-semibold text-[11px]">${isEn ? 'Reorder Needed' : 'Bestellvorschlag aktiv'}</span>
            </div>
          </div>

          <!-- RfQ / Price Comparison Banner -->
          <div class="p-3 rounded-xl bg-surface-3 border border-border flex items-center justify-between">
            <div>
              <span class="font-bold text-text-primary block">${isEn ? 'RfQ Price Comparison (3 Suppliers)' : 'Bestellanfrage RfQ (3 Lieferanten verglichen)'}</span>
              <span class="text-text-muted text-[10px]">${isEn ? 'Best Price: €42.50/unit • Fastest: 2 Days' : 'Bester Preis: 42,50 € / Stk. • Schnellste Lieferzeit: 2 Tage'}</span>
            </div>
            <span class="px-3 py-1.5 rounded-lg bg-primary text-white font-semibold text-[11px] shadow-sm cursor-pointer">
              ${isEn ? 'Create Order' : 'Bestellung anlegen'}
            </span>
          </div>
        </div>
      `;
    } else if (tab === 'financeDatev' || tab === 'ocr') {
      return `
        <div class="glass-card p-6 border border-border space-y-4 font-sans text-xs">
          <div class="flex items-center justify-between border-b border-border pb-4">
            <div>
              <span class="badge-brand px-2.5 py-1 rounded-full text-[11px]">${isEn ? 'DATEV Handover Cockpit' : 'DATEV-Kanzleiportal'}</span>
              <h4 class="text-base font-bold text-text-primary mt-1">${isEn ? 'Tax Advisor Batch: August 2026' : 'Steuerberater-Export: August 2026'}</h4>
              <p class="text-text-muted">${isEn ? 'EXTF Buchungsstapel with linked document images' : 'EXTF Buchungsstapel mit verknüpften Belegbildern'}</p>
            </div>
            <span class="px-3 py-1 rounded-full bg-success/15 text-success font-semibold text-[11px]">${isEn ? 'Advisor Portal Active' : 'Kanzlei-Portal Aktiv'}</span>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="p-3 rounded-xl bg-surface-2 border border-border">
              <span class="text-text-muted block text-[10px]">${isEn ? 'Pre-booked Postings' : 'Vorkontierte Belege'}</span>
              <span class="font-bold text-text-primary text-sm">${isEn ? '48 Documents (100%)' : '48 Belege (100%)'}</span>
              <span class="text-success text-[10px] block mt-0.5">${isEn ? 'SKR03 / SKR04 Mapped' : 'SKR03 / SKR04 Zugeordnet'}</span>
            </div>
            <div class="p-3 rounded-xl bg-surface-2 border border-border">
              <span class="text-text-muted block text-[10px]">${isEn ? 'Belegfeld 1 Binding' : 'Belegfeld 1 Verknüpfung'}</span>
              <span class="font-bold text-text-primary text-sm">${isEn ? 'ZUGFeRD & XRechnung' : 'ZUGFeRD & XRechnung'}</span>
              <span class="text-primary text-[10px] block mt-0.5">${isEn ? 'Digital archive verified' : 'Digitalarchiv verifiziert'}</span>
            </div>
          </div>

          <div class="p-3 rounded-xl bg-surface-2 border border-border space-y-2">
            <div class="flex justify-between font-bold text-text-primary">
              <span>${isEn ? 'Kanzlei Login: Dr. Weber & Partner mbB' : 'Kanzlei-Login: Dr. Weber & Partner mbB'}</span>
              <span class="text-success text-[11px]">✓ ${isEn ? 'Verified' : 'Bereitgestellt'}</span>
            </div>
            <p class="text-text-muted text-[11px]">
              ${isEn ? 'Your tax advisor downloads monthly ZIP batches directly. Retroactive changes export automatically as reversals (Generalumkehr).' : 'Dein Steuerberater lädt monatliche Buchungsstapel eigenständig herunter. Spätere Belegänderungen werden automatisch als Generalumkehr übergeben.'}
            </p>
          </div>
        </div>
      `;
    } else if (tab === 'customers') {
      return `
        <div class="glass-card p-6 border border-border space-y-4 font-sans text-xs">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-primary/20 text-primary flex items-center justify-center font-bold text-base">
                AC
              </div>
              <div>
                <h4 class="text-base font-bold text-text-primary">Acme Corp Germany GmbH</h4>
                <p class="text-text-muted">${isEn ? 'Customer No: KD-00104 • Frankfurt, Germany' : 'Kundennr: KD-00104 • Frankfurt, Deutschland'}</p>
              </div>
            </div>
            <span class="px-2.5 py-1 rounded-full bg-primary/10 text-primary font-semibold">${isEn ? 'DE Legal Form: GmbH' : 'DE Rechtsform: GmbH'}</span>
          </div>
          <div class="grid grid-cols-2 gap-3 pt-2">
            <div class="p-3 rounded-xl bg-surface-2">
              <span class="text-text-muted block text-[10px]">${isEn ? 'VAT ID Status' : 'USt-ID Status'}</span>
              <span class="font-bold text-success text-xs">${isEn ? 'DE 345 892 101 (Valid)' : 'DE 345 892 101 (Gültig)'}</span>
            </div>
            <div class="p-3 rounded-xl bg-surface-2">
              <span class="text-text-muted block text-[10px]">${isEn ? 'GDPR Consent' : 'DSGVO Einwilligung'}</span>
              <span class="font-bold text-text-primary text-xs">${isEn ? 'Confirmed (Opt-in)' : 'Bestätigt (Opt-in)'}</span>
            </div>
          </div>
          <div class="p-3 rounded-xl bg-surface-2 flex justify-between items-center">
            <div>
              <span class="font-bold text-text-primary block">${isEn ? 'Contract Management' : 'Vertragsverwaltung'}</span>
              <span class="text-text-muted text-[10px]">${isEn ? 'Maintenance Agreement 2026/27 • Notice: 3 Months' : 'Wartungsvertrag 2026/27 • Kündigungsfrist: 3 Monate'}</span>
            </div>
            <span class="px-2 py-0.5 rounded bg-success/20 text-success text-[10px] font-semibold">${isEn ? 'Active' : 'Aktiv'}</span>
          </div>
        </div>
      `;
    } else if (tab === 'time') {
      return `
        <div class="glass-card p-6 border border-border space-y-4 font-sans text-xs">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="w-3 h-3 rounded-full bg-danger animate-pulse"></span>
              <span class="font-bold text-text-primary text-sm">${isEn ? 'Live Timer: 03:42:15' : 'Live Timer: 03:42:15'}</span>
            </div>
            <span class="px-2.5 py-1 rounded-full bg-success/20 text-success font-semibold inline-flex items-center gap-1">${isEn ? 'Monthly Close Sealed' : 'Monatsabschluss Versiegelt'} <svg class="w-3.5 h-3.5 text-success inline-block" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/></svg></span>
          </div>
          <div class="p-3 rounded-xl bg-surface-2 space-y-2">
            <div class="flex justify-between text-text-primary font-medium">
              <span>${isEn ? 'Project: Mobile App Redesign' : 'Projekt: Mobile App Redesign'}</span>
              <span>${isEn ? 'Service: Backend API' : 'Leistungsart: Backend API'}</span>
            </div>
            <div class="w-full bg-surface-3 h-2 rounded-full overflow-hidden">
              <div class="bg-primary h-full w-[78%]"></div>
            </div>
            <div class="flex justify-between text-text-muted text-[10px]">
              <span>${isEn ? 'Timesheet PDF ready' : 'Stundennachweis PDF bereit'}</span>
              <span>${isEn ? 'Conflict Engine: No Overlaps' : 'Konfliktprüfungs-Engine: Keine Overlaps'}</span>
            </div>
          </div>
          <div class="p-2.5 rounded-lg bg-surface-3 border border-border flex justify-between items-center text-[11px]">
            <span class="text-text-primary font-medium">${isEn ? '12.5 billable hours ready to invoice' : '12,5 abrechenbare Stunden bereit zur Rechnungsübernahme'}</span>
            <span class="text-primary font-bold cursor-pointer">${isEn ? 'Take over →' : 'Übernehmen →'}</span>
          </div>
        </div>
      `;
    } else {
      // tab === 'ecosystem' or legacy 'sales' / 'ai'
      return `
        <div class="glass-card p-6 border border-border space-y-4 font-sans text-xs">
          <div class="flex items-center justify-between border-b border-border pb-3">
            <div>
              <span class="badge-brand px-2.5 py-1 rounded-full text-[11px]">${isEn ? 'Pipeline & Integrations' : 'Vertrieb & Schnittstellen'}</span>
              <h4 class="text-base font-bold text-text-primary mt-1">${isEn ? 'Sales Pipeline & Public REST API' : 'Vertriebs-Pipeline & Public REST API'}</h4>
            </div>
            <span class="px-2.5 py-1 rounded-full bg-primary/10 text-primary font-semibold text-[11px]">${isEn ? 'Mobile-optimized' : 'Mobil optimiert'}</span>
          </div>

          <div class="grid grid-cols-3 gap-2">
            <div class="p-2 rounded-lg bg-surface-2 border border-border">
              <span class="font-semibold text-text-secondary block text-[11px]">${isEn ? 'Lead (3)' : 'Erstkontakt (3)'}</span>
              <div class="mt-1.5 p-1.5 rounded bg-surface border border-border font-bold text-text-primary text-[10px]">
                TechCorp AG (12k €)
              </div>
            </div>
            <div class="p-2 rounded-lg bg-surface-2 border border-border">
              <span class="font-semibold text-text-secondary block text-[11px]">${isEn ? 'Proposal (2)' : 'Angebot (2)'}</span>
              <div class="mt-1.5 p-1.5 rounded bg-surface border border-border font-bold text-text-primary text-[10px]">
                Global Log (45k €)
              </div>
            </div>
            <div class="p-2 rounded-lg bg-surface-2 border border-border">
              <span class="font-semibold text-success block text-[11px]">${isEn ? 'Won (8)' : 'Gewonnen (8)'}</span>
              <div class="mt-1.5 p-1.5 rounded bg-surface border border-border font-bold text-text-primary text-[10px]">
                Enterprise (89k €)
              </div>
            </div>
          </div>

          <!-- API & Marketplace Snippet -->
          <div class="p-3 rounded-xl bg-surface-2 border border-border space-y-1.5 font-mono text-[11px]">
            <div class="flex justify-between items-center text-text-primary">
              <span class="text-primary font-bold">POST /api/v1/invoices</span>
              <span class="text-success font-semibold">201 Created</span>
            </div>
            <p class="text-text-muted font-sans text-[10px]">
              ${isEn ? 'API-Key authorized • Idempotency-Key validated • Webshop integration active' : 'API-Key autorisiert • Idempotency-Key validiert • Webshop-Anbindung aktiv'}
            </p>
          </div>
        </div>
      `;
    }
  }

  private initPricingToggle(): void {
    const monthlyBtn = document.getElementById('billing-monthly-btn');
    const yearlyBtn = document.getElementById('billing-yearly-btn');

    monthlyBtn?.addEventListener('click', () => {
      this.billingInterval = 'monthly';
      this.updatePricingToggleUI();
    });

    yearlyBtn?.addEventListener('click', () => {
      this.billingInterval = 'yearly';
      this.updatePricingToggleUI();
    });

    this.updatePricingToggleUI();
  }

  private updatePricingToggleUI(): void {
    const monthlyBtn = document.getElementById('billing-monthly-btn');
    const yearlyBtn = document.getElementById('billing-yearly-btn');

    if (this.billingInterval === 'yearly') {
      yearlyBtn?.classList.add('bg-primary', 'text-white', 'shadow');
      yearlyBtn?.classList.remove('text-text-secondary');
      monthlyBtn?.classList.remove('bg-primary', 'text-white', 'shadow');
      monthlyBtn?.classList.add('text-text-secondary');
    } else {
      monthlyBtn?.classList.add('bg-primary', 'text-white', 'shadow');
      monthlyBtn?.classList.remove('text-text-secondary');
      yearlyBtn?.classList.remove('bg-primary', 'text-white', 'shadow');
      yearlyBtn?.classList.add('text-text-secondary');
    }

    updateRegistrationInterval(document, this.billingInterval);
    this.updatePricingPrices();
  }

  /**
   * The live price book, read once (PRD-1553): the build baked the version
   * active at build time, so a price published since shows without a
   * deployment. A failed read keeps the baked amounts — and says nothing.
   */
  private initLivePricing(): void {
    if (!document.getElementById('price-standard') && !document.querySelector('[data-i18n^="marketplace.apps"]')) return;
    void fetchActivePricing().then((pricing) => {
      if (pricing) this.i18n.setPricing(pricing);
    });
  }

  private updatePricingPrices(): void {
    const dict = this.i18n.getTranslations();
    const isYearly = this.billingInterval === 'yearly';

    const freePrice = document.getElementById('price-free');
    const standardPrice = document.getElementById('price-standard');
    const businessPrice = document.getElementById('price-business');
    const standardAnnualNote = document.getElementById('price-standard-annual-note');
    const businessAnnualNote = document.getElementById('price-business-annual-note');

    if (freePrice) {
      freePrice.textContent = isYearly ? dict.pricing.free.priceYearly : dict.pricing.free.priceMonthly;
    }
    if (standardPrice) {
      standardPrice.textContent = isYearly ? dict.pricing.standard.priceYearly : dict.pricing.standard.priceMonthly;
    }
    if (businessPrice) {
      businessPrice.textContent = isYearly ? dict.pricing.business.priceYearly : dict.pricing.business.priceMonthly;
    }
    // Mirrors the app's checkout screen, which always states the annual total
    // alongside the per-month rate rather than leaving it for the payment page.
    standardAnnualNote?.classList.toggle('hidden', !isYearly);
    businessAnnualNote?.classList.toggle('hidden', !isYearly);

    // Add-on seats follow the same switch: on an annual term a full seat costs
    // less per month, just as the base price does.
    const seats = dict.pricing.extraSeats;
    document.querySelectorAll<HTMLElement>('[data-seat-price]').forEach((el) => {
      const seatClass = el.dataset['seatPrice'] === 'mobile' ? 'mobile' : 'full';
      el.textContent = isYearly ? seats[`${seatClass}Yearly`] : seats[seatClass];
    });
  }

}

// Initialize application on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new Application();
});
